import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtNum } from "../lib/api";
import { PageHeader, StatusBadge, Stat, Section, Pager, statusLabel } from "../components/common";
import { ScheduleEditor, VarChips } from "../components/CampaignForms";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Play, Pause, Square, Rocket, FlaskConical, Sparkles, Trash2, ArrowLeft, Eye, MousePointerClick } from "lucide-react";

const ALL = "__all";

function Preview({ cid, ccId, step, refresh }) {
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/campaigns/${cid}/preview`, { params: { cc_id: ccId || "", step } }).then((r) => setP(r.data)); }, [cid, ccId, step, refresh]);
  if (!p) return null;
  return (
    <div className="border border-slate-200 rounded-md" data-testid="email-preview">
      <div className="px-5 py-3 border-b border-slate-200 text-sm"><span className="text-slate-400 mr-2">Objet</span>{p.subject}</div>
      <div className="px-5 py-3 border-b border-slate-100 text-xs text-slate-500">À : {p.cc ? `${p.cc.first_name} ${p.cc.last_name} <${p.cc.email}>` : "exemple (aucun contact ajouté)"}</div>
      <div className="px-5 py-5 email-preview text-sm" dangerouslySetInnerHTML={{ __html: p.html }} />
    </div>
  );
}

function ContentTab({ c, onSaved }) {
  const [f, setF] = useState({ subject: c.subject, body: c.body, followup_body: c.followup_body || "" });
  const [tick, setTick] = useState(0);
  const save = async () => {
    try { await api.put(`/campaigns/${c.id}`, { ...c, ...f }); toast.success("Contenu enregistré"); setTick((t) => t + 1); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div className="space-y-1"><Label className="text-xs">Objet</Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="campaign-subject" /></div>
        <div className="space-y-1"><Label className="text-xs">Email initial</Label><Textarea rows={14} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="font-mono text-[12.5px]" data-testid="campaign-body" /></div>
        <VarChips onInsert={(v) => setF({ ...f, body: f.body + v })} />
        <div className="space-y-1"><Label className="text-xs">Texte des relances (envoyées dans le même fil)</Label><Textarea rows={8} value={f.followup_body} onChange={(e) => setF({ ...f, followup_body: e.target.value })} className="font-mono text-[12.5px]" data-testid="campaign-followup-body" /></div>
        <p className="text-xs text-slate-500">Un pied de page de désinscription et l'identité de l'expéditeur sont ajoutés automatiquement.</p>
        <Button onClick={save} data-testid="campaign-content-save">Enregistrer le contenu</Button>
      </div>
      <div className="space-y-4">
        <div className="eyebrow">Aperçu — email initial</div>
        <Preview cid={c.id} step={0} refresh={tick} />
        <div className="eyebrow">Aperçu — relance</div>
        <Preview cid={c.id} step={1} refresh={tick} />
      </div>
    </div>
  );
}

function PlanningTab({ c, onSaved }) {
  const [sch, setSch] = useState(c.schedule);
  const [fu, setFu] = useState({ followup_count: c.followup_count, followup_delay_days: c.followup_delay_days });
  const save = async () => {
    try { await api.put(`/campaigns/${c.id}`, { ...c, ...fu, schedule: sch }); toast.success("Planification enregistrée"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6">
      <Section title="Horaires et cadence" description="Modifiable à tout moment, y compris pendant la campagne.">
        <ScheduleEditor value={sch} onChange={setSch} cap={c.global_daily_cap} />
      </Section>
      <Section title="Relances automatiques" description="Arrêtées immédiatement dès qu'une réponse, un rebond ou une désinscription est détecté.">
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div className="space-y-1"><Label className="text-xs">Nombre de relances (0–5)</Label><Input type="number" min={0} max={5} value={fu.followup_count} onChange={(e) => setFu({ ...fu, followup_count: Math.min(5, Number(e.target.value)) })} data-testid="followup-count" /></div>
          <div className="space-y-1"><Label className="text-xs">Délai entre relances (jours)</Label><Input type="number" min={1} value={fu.followup_delay_days} onChange={(e) => setFu({ ...fu, followup_delay_days: Number(e.target.value) })} data-testid="followup-delay" /></div>
        </div>
      </Section>
      <Button onClick={save} data-testid="planning-save">Enregistrer la planification</Button>
    </div>
  );
}

const TRK = { opened: "Ont ouvert", clicked: "Ont cliqué", opened_no_reply: "Ouvert sans réponse", clicked_no_reply: "Cliqué sans réponse", not_opened: "Envoyé, non ouvert" };
const EV = { sent: "Envoyé", open: "Ouverture", click: "Clic" };

function TrackCell({ t, id }) {
  if (!t) return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className="text-xs whitespace-nowrap" data-testid={`cc-tracking-${id}`}>
      <span className={t.opens ? "text-emerald-700" : "text-slate-400"}><Eye className="h-3 w-3 inline mr-1" />{t.opens}</span>
      <span className={`ml-3 ${t.clicks ? "text-[#0b1f3a] font-medium" : "text-slate-400"}`}><MousePointerClick className="h-3 w-3 inline mr-1" />{t.clicks}</span>
      {(t.last_click_at || t.last_open_at) && <div className="text-[10px] text-slate-400">{fmtDate(t.last_click_at > (t.last_open_at || "") ? t.last_click_at : t.last_open_at)}</div>}
    </div>
  );
}

function TrackHistory({ t }) {
  return (
    <div className="border border-slate-200 rounded-md px-4 py-3" data-testid="cc-tracking-history">
      <div className="eyebrow mb-2">Historique du suivi</div>
      {!t ? <div className="text-sm text-slate-500">Aucun email envoyé pour le moment.</div> : (
        <ul className="space-y-1.5 text-sm max-h-48 overflow-y-auto">
          {t.events.map((e, i) => (
            <li key={i} className="flex justify-between gap-4" data-testid={`cc-event-${i}`}>
              <span>{EV[e.type]} <span className="text-slate-400 text-xs">· {e.step === 0 ? "email initial" : `relance ${e.step}`}</span>{e.url && <span className="block text-[11px] text-slate-500 truncate max-w-[340px]">{e.url}</span>}</span>
              <span className="text-xs text-slate-400 shrink-0">{fmtDate(e.at)}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-slate-400 mt-2">Les ouvertures sont estimées (certaines messageries bloquent ou préchargent les images). Les clics sont fiables.</p>
    </div>
  );
}

function ContactsTab({ c, onChanged }) {
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState("");
  const [status, setStatus] = useState(ALL);
  const [trk, setTrk] = useState(ALL);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [sel, setSel] = useState(null);
  const [perso, setPerso] = useState("");
  const load = useCallback(() => {
    api.get(`/campaigns/${c.id}/contacts`, { params: { page, status: status === ALL ? "" : status, tracking: trk === ALL ? "" : trk } }).then((r) => setData(r.data));
  }, [c.id, page, status, trk]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/lists").then((r) => setLists(r.data)); }, []);
  const add = async () => {
    try {
      const r = await api.post(`/campaigns/${c.id}/contacts`, { list_id: listId });
      const s = r.data;
      toast.success(`${s.added} ajoutés · ${s.company_duplicate} déjà dans la campagne · ${s.not_eligible} non éligibles · ${s.already_contacted} déjà contactés · ${s.in_other_campaign} dans une autre campagne · ${s.suppressed} exclus`, { duration: 8000 });
      load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const patch = async (cc, body, ok) => { try { await api.patch(`/campaigns/${c.id}/contacts/${cc.id}`, body); toast.success(ok); load(); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const personalize = async () => { await api.post(`/campaigns/${c.id}/personalize`); toast.success("Personnalisation IA en cours (contacts en file)"); setTimeout(load, 8000); };
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1"><Label className="text-xs">Ajouter les décideurs éligibles d'une liste</Label>
          <Select value={listId} onValueChange={setListId}><SelectTrigger className="w-64" data-testid="campaign-add-list-select"><SelectValue placeholder="Choisir une liste" /></SelectTrigger>
            <SelectContent>{lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} ({l.with_contact})</SelectItem>)}</SelectContent></Select>
        </div>
        <Button disabled={!listId} onClick={add} data-testid="campaign-add-contacts-btn">Ajouter</Button>
        <Button variant="outline" onClick={personalize} data-testid="campaign-ai-personalize-btn"><Sparkles className="h-4 w-4 mr-1" />Personnaliser par IA</Button>
        <div className="ml-auto flex gap-2">
          <Select value={trk} onValueChange={(v) => { setPage(1); setTrk(v); }}><SelectTrigger className="w-56" data-testid="campaign-contacts-tracking"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>Tout le suivi</SelectItem>{Object.entries(TRK).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`tracking-opt-${k}`}>{l}</SelectItem>)}</SelectContent></Select>
          <Select value={status} onValueChange={(v) => { setPage(1); setStatus(v); }}><SelectTrigger className="w-44" data-testid="campaign-contacts-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>Tous statuts</SelectItem>{["queued", "sent", "replied", "bounced", "unsubscribed", "needs_review", "failed", "completed", "excluded", "duplicate"].map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>
      <p className="text-xs text-slate-500">Règles appliquées : un seul contact par entreprise, exclusion des contacts non vérifiés, déjà sollicités, présents dans une autre campagne ou en liste d'opposition. La personnalisation IA n'utilise que les données enregistrées (aucun fait inventé) et est visible dans l'aperçu.</p>
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="campaign-contacts-table">
          <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr>{["Contact", "Entreprise", "Email", "Statut", "Ouvert / Cliqué", "Étape", "Prochaine relance", "Personnalisation", ""].map((h) => <th key={h} className="px-4 py-2.5 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {data.items.map((cc) => (
              <tr key={cc.id} className="border-t border-slate-100 row-hover">
                <td className="px-4 py-2.5">{cc.first_name} {cc.last_name}<div className="text-xs text-slate-400">{cc.title}</div></td>
                <td className="px-4">{cc.company_name}</td>
                <td className="px-4 font-mono text-xs">{cc.email}</td>
                <td className="px-4"><StatusBadge status={cc.status} />{cc.reason && <div className="text-[10px] text-slate-500 max-w-[200px]">{cc.reason}</div>}</td>
                <td className="px-4"><TrackCell t={cc.tracking} id={cc.id} /></td>
                <td className="px-4 tabular">{cc.step >= 0 ? (cc.step === 0 ? "Initial" : `Relance ${cc.step}`) : "—"}</td>
                <td className="px-4 text-xs">{fmtDate(cc.next_followup_at)}</td>
                <td className="px-4 text-xs text-slate-600 max-w-[220px] truncate">{cc.personalization || "—"}</td>
                <td className="px-3 whitespace-nowrap">
                  <button className="text-xs underline text-slate-600 mr-2" onClick={() => { setSel(cc); setPerso(cc.personalization || ""); }} data-testid={`cc-view-${cc.id}`}>Aperçu</button>
                  {cc.status === "needs_review" && <>
                    <button className="text-xs underline text-slate-600 mr-2" onClick={() => patch(cc, { action: "mark_sent" }, "Marqué comme envoyé")}>Confirmé envoyé</button>
                    <button className="text-xs underline text-slate-600 mr-2" onClick={() => window.confirm("Confirmez-vous que cet email n'a PAS été envoyé (vérifié dans Outlook) ?") && patch(cc, { action: "requeue" }, "Remis en file")}>Remettre en file</button>
                  </>}
                  {["queued", "needs_review", "failed"].includes(cc.status) && <button onClick={() => patch(cc, { action: "remove" }, "Retiré")} className="text-slate-400 hover:text-red-600 align-middle" data-testid={`cc-remove-${cc.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={page} total={data.total} onChange={setPage} />
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cc-preview-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Aperçu personnalisé</DialogTitle></DialogHeader>
          {sel && <>
            <div className="space-y-1"><Label className="text-xs">Phrase de personnalisation ({"{personalization}"})</Label>
              <div className="flex gap-2"><Input value={perso} onChange={(e) => setPerso(e.target.value)} data-testid="cc-personalization-input" />
                <Button variant="outline" onClick={async () => { await patch(sel, { personalization: perso }, "Personnalisation enregistrée"); setSel({ ...sel, personalization: perso, _t: Date.now() }); }} data-testid="cc-personalization-save">Enregistrer</Button></div>
            </div>
            <TrackHistory t={sel.tracking} />
            <Preview cid={c.id} ccId={sel.id} step={0} refresh={sel._t} />
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LaunchDialog({ c, open, onClose, onDone }) {
  const [txt, setTxt] = useState("");
  const [checks, setChecks] = useState({});
  const items = [
    ["content", "J'ai relu l'objet, le contenu et l'aperçu personnalisé."],
    ["contacts", `J'ai vérifié les ${c.counts.queued || 0} contacts en file d'attente.`],
    ["schedule", `Cadence validée : ${c.schedule.daily_limit} emails/jour max, répartis automatiquement sur ${c.schedule.windows.map((w) => `${w.start}–${w.end}`).join(" et ")}.`],
    ["legal", "Cette prospection respecte la LPD, le RGPD le cas échéant et les règles locales (intérêt légitime B2B, opposition facile)."],
  ];
  const ok = items.every(([k]) => checks[k]) && txt.trim().toUpperCase() === "LANCER";
  const go = async () => {
    try { await api.post(`/campaigns/${c.id}/launch`, { confirm: true, confirmation_text: txt }); toast.success("Campagne lancée"); onDone(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="launch-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Valider le lancement</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {items.map(([k, l]) => (
            <label key={k} className="flex items-start gap-3 text-sm cursor-pointer">
              <Checkbox checked={!!checks[k]} onCheckedChange={(v) => setChecks({ ...checks, [k]: !!v })} data-testid={`launch-check-${k}`} className="mt-0.5" />{l}
            </label>
          ))}
          <div className="space-y-1 pt-2"><Label className="text-xs">Saisissez <b>LANCER</b> pour confirmer</Label><Input value={txt} onChange={(e) => setTxt(e.target.value)} data-testid="launch-confirm-input" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annuler</Button><Button disabled={!ok} onClick={go} data-testid="launch-confirm-btn"><Rocket className="h-4 w-4 mr-1" />Lancer la campagne</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [launch, setLaunch] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testOpen, setTestOpen] = useState(false);
  const load = useCallback(() => api.get(`/campaigns/${id}`).then((r) => setC(r.data)), [id]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  if (!c) return <div className="text-sm text-slate-500">Chargement…</div>;
  const action = async (a) => {
    if (a === "stop" && !window.confirm("Arrêter définitivement la campagne ? Les envois en file et les relances seront annulés.")) return;
    try { await api.post(`/campaigns/${id}/control/${a}`); toast.success("Commande appliquée"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const testSend = async () => {
    try { await api.post(`/campaigns/${id}/test-send`, { to: testTo, confirm: true }); toast.success("Email de test envoyé"); setTestOpen(false); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => { if (!window.confirm("Supprimer cette campagne ?")) return; try { await api.delete(`/campaigns/${id}`); nav("/campaigns"); } catch (e) { toast.error(errMsg(e)); } };
  const k = c.counts;
  return (
    <div>
      <button onClick={() => nav("/campaigns")} className="text-xs text-slate-500 flex items-center gap-1 mb-4 hover:text-[#0b1f3a]"><ArrowLeft className="h-3 w-3" />Campagnes</button>
      <PageHeader eyebrow="Campagne" title={c.name}
        description={<span className="flex items-center gap-2"><StatusBadge status={c.status} testid="campaign-status" />{c.pause_reason && <span className="text-amber-800">{c.pause_reason}</span>}{c.launched_at && <span>Validée le {fmtDate(c.launched_at)} par {c.launched_by}</span>}</span>}
        actions={<>
          <Button variant="outline" onClick={() => setTestOpen(true)} data-testid="test-send-btn"><FlaskConical className="h-4 w-4 mr-1" />Email de test</Button>
          {(c.status === "draft" || (c.status === "paused" && !c.launched_at)) && <Button onClick={() => setLaunch(true)} data-testid="launch-btn"><Rocket className="h-4 w-4 mr-1" />Lancer</Button>}
          {c.status === "active" && <Button variant="outline" onClick={() => action("pause")} data-testid="pause-btn"><Pause className="h-4 w-4 mr-1" />Pause</Button>}
          {c.status === "paused" && c.launched_at && <Button onClick={() => action("resume")} data-testid="resume-btn"><Play className="h-4 w-4 mr-1" />Reprendre</Button>}
          {["active", "paused"].includes(c.status) && <Button variant="destructive" onClick={() => action("stop")} data-testid="stop-btn"><Square className="h-4 w-4 mr-1" />Arrêter</Button>}
          {["draft", "stopped", "completed"].includes(c.status) && <Button variant="ghost" onClick={del} data-testid="delete-campaign-btn"><Trash2 className="h-4 w-4" /></Button>}
        </>} />
      {c.global_pause && <div className="mb-6 text-sm border border-red-200 bg-red-50 text-red-800 rounded px-4 py-2">Envois globalement suspendus (Paramètres → Envoi). Aucune campagne n'envoie.</div>}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-8">
        <Stat label="Contacts" value={fmtNum(k.total)} testid="cstat-total" />
        <Stat label="En file" value={fmtNum(k.queued)} testid="cstat-queued" />
        <Stat label="Emails envoyés" value={fmtNum(k.emails_sent)} testid="cstat-sent" />
        <Stat label="Aujourd'hui" value={`${c.sent_today}/${c.schedule.daily_limit}`} hint={c.in_window ? "Dans la plage horaire" : "Hors plage horaire"} testid="cstat-today" />
        <Stat label="Réponses" value={fmtNum(k.replied)} testid="cstat-replied" />
        <Stat label="Rebonds" value={fmtNum(k.bounced)} testid="cstat-bounced" />
        <Stat label="À vérifier" value={fmtNum(k.needs_review)} testid="cstat-review" />
      </div>
      <Tabs defaultValue="contacts">
        <TabsList className="mb-6">
          <TabsTrigger value="contacts" data-testid="ctab-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="content" data-testid="ctab-content">Contenu & aperçu</TabsTrigger>
          <TabsTrigger value="planning" data-testid="ctab-planning">Planification & relances</TabsTrigger>
        </TabsList>
        <TabsContent value="contacts"><ContactsTab c={c} onChanged={load} /></TabsContent>
        <TabsContent value="content"><ContentTab c={c} onSaved={load} /></TabsContent>
        <TabsContent value="planning"><PlanningTab c={c} onSaved={load} /></TabsContent>
      </Tabs>
      <LaunchDialog c={c} open={launch} onClose={() => setLaunch(false)} onDone={load} />
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent data-testid="test-send-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Envoyer un email de test</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">L'email (personnalisé avec le premier contact) sera envoyé via votre compte Microsoft 365 à l'adresse indiquée uniquement, avec l'objet préfixé [TEST].</p>
          <Input type="email" placeholder="votre.adresse@campusholdings.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="test-send-to" />
          <DialogFooter><Button variant="outline" onClick={() => setTestOpen(false)}>Annuler</Button><Button disabled={!testTo} onClick={testSend} data-testid="test-send-confirm">J'autorise l'envoi du test</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
